'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import LeadCaptureModal from './components/LeadCaptureModal';

const HERO_HEADLINES = [
  { first: 'Recover the commission you earned,', second: 'but never received.', greenSecond: true },
  {
    first: 'Medicare carriers forget to pay agents 3–5% of their renewals.',
    second: 'Tally finds your missing money in seconds.',
    greenSecond: true,
  },
  { first: 'Stop drowning in spreadsheets.', second: 'Audit every payment in seconds.', greenSecond: true },
];
const ROTATE_INTERVAL_MS = 6_000;
import MarketingPricingSection from './components/MarketingPricingSection';
import MarketingFeatureStrip from './components/MarketingFeatureStrip';
import ComparisonSection from './components/ComparisonSection';
import MarketingFAQSection from './components/MarketingFAQSection';
import MarketingFooter from './components/MarketingFooter';

export default function RootMarketingPage() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroIndex((i) => (i + 1) % HERO_HEADLINES.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen flex flex-col text-slate-900 overflow-x-hidden">
      {/* Top urgency banner – static copy, no countdown logic yet */}
      <div className="bg-[#b45309] text-white text-center py-2 font-heading text-xs sm:text-sm tracking-[0.18em] uppercase">
        EXCLUSIVE OFFER: LOCK IN $20/$40/MO FOREVER FOR THE FIRST 50 SUBSCRIBERS
      </div>
      <LeadCaptureModal />
      <header className="sticky top-0 z-[110] bg-white/95 backdrop-blur shrink-0 border-b border-slate-200/80">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center h-[4.356rem] pl-2 sm:pl-0 -ml-[10px] sm:ml-0"
            onClick={() => setMobileNavOpen(false)}
          >
            <Image
              src="/tallymedicare-lockup.png"
              alt="TallyMedicare"
              width={262}
              height={71}
              className="h-[4.356rem] w-auto object-contain"
            />
          </a>

          <div className="flex items-center gap-3 text-base ml-auto">
            {/* Mobile: hamburger to open nav */}
            <button
              type="button"
              className="md:hidden p-2 rounded-md hover:bg-slate-100 text-slate-600"
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <a
              href="/login-authentication"
              className="hidden sm:inline-flex px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 text-slate-700 transition-colors bg-white"
            >
              Sign in
            </a>
            <a
              href="/public-subscription-selection"
              className="hidden sm:inline-flex whitespace-nowrap px-3 py-2 rounded-md bg-[#236434] text-white font-semibold text-sm sm:text-base hover:bg-[#1c522e] transition-colors"
            >
              Get early access
            </a>
          </div>
        </div>
        {/* Mobile nav overlay */}
        {mobileNavOpen && (
          <div className="md:hidden border-t border-slate-200/80 bg-white">
            <nav className="mx-auto max-w-6xl px-4 py-4 flex flex-col gap-1">
              <a
                href="#features"
                className="py-3 text-slate-700 font-medium hover:text-[#236434]"
                onClick={() => setMobileNavOpen(false)}
              >
                Features
              </a>
              <a
                href="#pricing"
                className="py-3 text-slate-700 font-medium hover:text-[#236434]"
                onClick={() => setMobileNavOpen(false)}
              >
                Pricing
              </a>
              <a
                href="#faq"
                className="py-3 text-slate-700 font-medium hover:text-[#236434]"
                onClick={() => setMobileNavOpen(false)}
              >
                FAQs
              </a>
            </nav>
          </div>
        )}
      </header>

      <div className="flex-1 flex flex-col">
        {/* Hero area: gradient so gray starts at CTA buttons; height from content so features stay right below */}
        <div className="shrink-0 overflow-x-hidden bg-[linear-gradient(to_bottom,#b1d6f6_0%,#fff9f3_100%)]">
          <main className="mx-auto max-w-6xl px-4 pt-10 md:pt-20 pb-6 md:pb-8">
            <section className="flex flex-col items-center overflow-x-hidden text-center max-w-3xl mx-auto pt-8 md:pt-[60px]">
            {/* Headline area: tall enough for 4-line headline (e.g. "in seconds"); overflow hidden for slide */}
            <div
              className="relative w-full overflow-hidden mb-[32px] min-h-[15.5rem] sm:min-h-[13rem] md:min-h-[19rem]"
              aria-live="polite"
            >
              {HERO_HEADLINES.map((h, i) => {
                const isActive = heroIndex === i;
                const translateX =
                  isActive ? 0 : i < heroIndex ? 100 : -100;
                return (
                  <h1
                    key={i}
                    className={`absolute left-0 right-0 top-0 flex min-h-[15rem] sm:min-h-[13rem] md:min-h-[19rem] w-full items-center justify-center px-3 pt-6 pb-6 sm:pt-8 sm:pb-8 text-center font-poppins !font-bold sm:!font-semibold leading-[1.25] tracking-tight transition-transform duration-500 ease-in-out ${
                      i === 1 ? 'text-[28px] sm:text-[30px] md:text-[46px]' : 'text-[30px] sm:text-[34px] md:text-[53px]'
                    }`}
                    style={{ transform: `translateX(${translateX}%)` }}
                    aria-hidden={!isActive}
                  >
                    <span className="block px-1">
                      <span className="text-slate-900">{h.first}</span>
                      {h.second != null && (
                        <>
                          {' '}
                          <span className={h.greenSecond ? 'text-[#236434]' : 'text-slate-900'}>
                            {h.second}
                          </span>
                        </>
                      )}
                    </span>
                  </h1>
                );
              })}
            </div>

            <p className="text-base md:text-lg text-slate-900 max-w-2xl mb-[44px]">
              Between rapid disenrollments and clerical errors, 10% of agent revenue is lost to leakage.{' '}
              <span className="font-semibold">Our Earning Recovery engine identifies missing payments and generates the proof you need to win your dispute.</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6 justify-center">
              <a
                href="/login-authentication"
                className="inline-flex justify-center px-8 py-4 rounded-md bg-white text-slate-800 text-lg hover:bg-slate-50 transition-colors"
              >
                Sign in
              </a>
              <a
                href="/public-subscription-selection"
                className="inline-flex justify-center px-12 py-4 rounded-md bg-[#ef601e] text-white font-semibold text-lg hover:bg-[#d9551a] transition-colors"
              >
                Reclaim my commissions
              </a>
            </div>

            <p className="text-[13px] text-[#236434] mt-6 mb-2 text-center">
              No long-term contracts. Built by Medicare brokers for independent brokers and agencies.
            </p>
            </section>

            {/* Laptop / dashboard hero image with missing-commission callout */}
            <section className="mt-10 md:mt-14">
              <div className="mx-auto max-w-6xl px-0 md:px-4 flex justify-center">
                <div className="relative w-full max-w-[65rem] rounded-2xl md:overflow-hidden transform scale-[1.18] md:scale-100">
                  <Image
                    src="/Dashboard_TransparentBG_Short.png"
                    alt="Tally dashboard overview"
                    width={1600}
                    height={712}
                    className="w-full h-auto object-cover"
                    priority
                  />

                  {/* Animated callout: missing commission found — slightly larger on mobile */}
                  <div className="absolute bottom-[24%] right-[4%] sm:right-[10%] max-w-[32%] sm:max-w-none bg-white rounded-lg sm:rounded-xl shadow-xl border border-slate-200 border-l-4 border-l-[#f97316] px-3 py-2.5 sm:px-6 sm:py-4 flex flex-col items-start animate-bounce">
                    <span className="text-[10px] sm:text-[13px] font-semibold tracking-[0.08em] sm:tracking-[0.16em] uppercase text-slate-400 leading-tight">
                      MISSING COMMISSION FOUND
                    </span>
                    <span className="mt-0.5 sm:mt-1.5 text-[18px] sm:text-[34px] font-heading font-extrabold text-[#f97316] leading-tight">
                      $4,120.60
                    </span>
                  </div>

                  {/* Animated callout: pending payments — slightly larger on mobile */}
                  <div className="absolute top-[58%] left-[6%] sm:left-[18%] max-w-[36%] sm:max-w-none bg-white rounded-lg sm:rounded-xl shadow-xl border border-slate-200 border-l-4 border-l-[#236434] px-3 py-2.5 sm:px-5 sm:py-3.5 flex flex-col items-start animate-bounce [animation-duration:2s]">
                    <span className="text-[10px] sm:text-[11px] font-semibold tracking-[0.06em] sm:tracking-[0.16em] uppercase text-slate-400 block leading-tight">
                      PENDING PAYMENTS
                      <br />
                      THIS MONTH
                    </span>
                    <span className="mt-0.5 sm:mt-1 text-[18px] sm:text-[26px] font-heading font-bold text-[#236434] leading-tight">
                      $12,000
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>

        {/* Rest of page: solid gray from CTA level down */}
        <div className="flex-1 bg-[#fff9f3]">
        <MarketingFeatureStrip />

        <div className="mx-auto max-w-6xl px-4">
        <ComparisonSection />

        {/* Smart brokers choose Tally: two images + headline + subhead */}
        <section className="mt-20 md:mt-36 flex flex-col items-center text-center max-w-4xl mx-auto px-4">
          <h2 className="font-poppins text-[32px] md:text-[53px] font-bold sm:font-semibold tracking-tight leading-[1.2] text-slate-900 mb-6">
            Smart brokers choose Tally
          </h2>
          <p className="text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed mb-10 mx-auto">
            Stop hoping and start auditing with the industry&apos;s most advanced automated tool to balance your book of business. We are constantly advancing our platform so you can lock in your Founder rate forever and secure your revenue with a perfect-match tool.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 w-full">
            {/* Smart broker — 2x asset, half size; clip 2px all sides to remove green edge; slight rounded corners (desktop left, mobile second) */}
            <div className="relative w-full flex justify-center sm:flex-1 sm:max-w-[460px] bg-[#fff9f3] overflow-hidden rounded-lg border-0 outline-none ring-0 order-2 sm:order-1">
              <div className="relative w-full max-w-[460px] overflow-hidden rounded-lg" style={{ aspectRatio: '921/1024' }}>
                <div className="absolute -left-[6px] -top-[6px] w-[calc(100%+12px)] h-[calc(100%+12px)] relative">
                  <Image
                    src="/smartbroker.png"
                    alt="Broker celebrating with Tally earnings report fully paid"
                    fill
                    className="object-contain object-center border-0 outline-none"
                    sizes="(max-width: 640px) 100vw, 460px"
                  />
                </div>
              </div>
            </div>
            {/* Oldschool broker — 2x asset, half size; clip to remove green edge; slight rounded corners (desktop right, mobile first) */}
            <div className="relative w-full flex justify-center sm:flex-1 sm:max-w-[460px] bg-[#fff9f3] overflow-visible rounded-lg border-0 outline-none ring-0 order-1 sm:order-2">
              <div className="relative w-full max-w-[460px] overflow-hidden rounded-lg" style={{ aspectRatio: '921/1024' }}>
                <div className="absolute -left-[10px] -top-[10px] w-[calc(100%+20px)] h-[calc(100%+20px)] relative overflow-hidden rounded-lg">
                  <Image
                    src="/oldschoolbroker.png"
                    alt="Broker reviewing manual ledger and statements"
                    fill
                    className="object-contain object-center border-0 outline-none scale-[1.02]"
                    sizes="(max-width: 640px) 100vw, 460px"
                  />
                </div>
                {/* Static callout over woman's arm — 30px right, sticks out from box; orange accent */}
                <div className="absolute bottom-[28%] left-[8%] right-[8%] sm:left-[12%] sm:right-auto sm:max-w-[75%] translate-y-[20px] translate-x-[100px] sm:translate-x-[150px] bg-white rounded-xl shadow-xl border border-slate-200 border-l-4 border-l-[#f97316] px-3 py-2.5 text-left">
                  <span className="text-[13px] font-semibold text-slate-400 leading-snug normal-case block">
                    Why did I only get paid $300
                    <br />
                    for a T65 client?
                  </span>
                  <span className="mt-1.5 text-lg md:text-xl font-bold text-[#f97316] block">
                    $394–$564 missing commission
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="mt-20 md:mt-36 pt-10 pb-0 flex flex-col items-center text-center">
          <h2 className="font-poppins text-[32px] md:text-[53px] font-bold sm:font-semibold tracking-tight leading-[1.2] max-w-2xl mb-[40px]">
            <span className="text-black">Be one of the first 50.</span>
            <br />
            <span className="text-[#236434]">Pay this price forever.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed">
            You&apos;re doing the hard work of building your book, we&apos;re here to make sure you actually get paid for it. Join our first 50 members and we&apos;ll lock in your current rate forever. <strong>As we add more features and automation, your price will never move a cent.</strong> No tiers, no hidden fees, just the only commission tool you&apos;ll ever have to buy.
          </p>
        </section>

        <MarketingPricingSection />

        <MarketingFAQSection />

        {/* End image after FAQ – phone mockup above footer, centered with floating callouts */}
        <section className="mt-20 md:mt-32 pb-0 flex flex-col items-center text-center -mb-32 sm:mb-0">
          <h2 className="font-poppins text-[32px] md:text-[53px] !font-bold sm:!font-semibold tracking-tight leading-[1.2] text-slate-900 max-w-4xl mx-auto mb-8 px-4">
            The audit you always needed,
            <br />
            now on autopilot.
          </h2>
          <a
            href="/public-subscription-selection"
            className="inline-flex justify-center px-10 py-5 rounded-md bg-[#236434] text-white font-semibold text-lg md:text-xl hover:bg-[#1c522e] transition-colors mt-[40px] mb-8 sm:mb-16"
          >
            Get paid for every enrollment
          </a>
          <div className="relative w-full max-w-5xl mx-auto px-4 flex justify-center items-center overflow-visible sm:overflow-x-hidden">
            <div className="relative flex justify-center items-center w-full origin-center scale-[0.75] sm:scale-100 overflow-visible">
              <Image
                src="/end_imagePhone.png"
                alt="Tally app on phone: expected revenue, bank deposits, carrier statements"
                width={400}
                height={720}
                className="relative z-0 w-full max-w-[332px] sm:max-w-[400px] h-auto object-contain"
                sizes="(max-width: 768px) 332px, 400px"
                quality={90}
              />
              {/* Callouts around phone */}
              <div className="absolute left-[4%] top-[6%] w-[38%] max-w-[170px] md:max-w-[210px] bg-[#ef601e] rounded-xl shadow-lg p-[8px] sm:p-[14px] text-center z-10 transform scale-[0.85] sm:scale-[1.1] -translate-x-[50px] sm:translate-x-0 translate-y-[120px]">
                <span className="text-[11px] sm:text-[15px] font-poppins font-semibold text-white leading-snug text-center block">
                  I know I have 400 clients on the books, so why does my renewal check look like I only have 320?
                  <br />
                  <span className="text-[#01442c]">Resolved by Tally</span>
                </span>
              </div>
              <div className="absolute left-[4%] top-[33%] w-[36%] max-w-[160px] md:max-w-[190px] bg-[#12d486] rounded-xl shadow-lg p-[8px] sm:p-2.5 text-center z-10 transform scale-[1.188] sm:scale-[1.32] -translate-x-[40px] sm:translate-x-[120px] translate-y-[140px]">
                <span className="text-[11px] sm:text-[15px] font-poppins font-semibold text-slate-900 leading-snug text-center block">
                  Automates the audit, tracks your Approved, Pending, and Rescued revenue
                </span>
              </div>
              <div className="absolute left-[4%] bottom-[20%] w-[34%] max-w-[150px] md:max-w-[185px] bg-[#12d486] rounded-xl shadow-lg p-[8px] sm:p-[14px] text-center z-10 transform scale-[1.346] sm:scale-[1.21] translate-y-[70px] translate-x-[-60px] sm:translate-x-[20px]">
                <span className="text-[12px] sm:text-[17px] font-poppins font-semibold text-slate-900 leading-snug text-center block">
                  Dispute-ready
                  <br />
                  recovery email
                </span>
              </div>
              <div className="absolute right-[4%] top-[10%] w-[40%] max-w-[185px] md:max-w-[215px] bg-[#ffda69] rounded-xl shadow-lg p-[10px] sm:p-[30px] text-center z-10 transform scale-[0.85] sm:scale-[1.1] -translate-x-[20px]">
                <span className="text-[10px] sm:text-[13px] font-poppins font-semibold text-slate-900 leading-snug text-center block">
                  Is this deposit lower because of a legitimate chargeback, or did a clerical error just cost me a month&apos;s rent?
                  <br />
                  <span className="text-[#01442c]">Resolved by Tally</span>
                </span>
              </div>
              <div className="absolute right-[4%] top-[37%] w-[36%] max-w-[165px] md:max-w-[195px] bg-[#12d486] rounded-xl shadow-lg p-[8px] sm:p-[14px] text-center z-10 transform scale-[1.271] sm:scale-[1.1] translate-x-[30px] sm:-translate-x-[120px]">
                <span className="text-[12px] sm:text-[17px] font-poppins font-semibold text-slate-900 leading-snug text-center block">
                  Finds your missing money in seconds
                </span>
              </div>
              <div className="absolute right-[4%] top-[57%] w-[40%] max-w-[185px] md:max-w-[215px] bg-[#ef601e] rounded-xl shadow-lg p-[8px] sm:p-[14px] text-center z-10 transform scale-[0.85] sm:scale-[1.1] translate-x-[120px] sm:translate-x-[50px]">
                <span className="text-[11px] sm:text-[15px] font-poppins font-semibold text-white leading-snug text-center block">
                  Did that client who turned 65 get coded as a full &apos;Initial&apos; payment, or did the carrier sneak it through as a prorated renewal?
                  <br />
                  <span className="text-[#01442c]">Resolved by Tally</span>
                </span>
              </div>
              <div className="absolute right-[4%] bottom-[18%] w-[38%] max-w-[175px] md:max-w-[205px] bg-[#12d486] rounded-xl shadow-lg p-[8px] sm:p-[14px] text-center z-10 transform scale-[0.85] sm:scale-[1.1] translate-y-[50px] translate-x-[50px] sm:-translate-x-[50px]">
                <span className="text-[12px] sm:text-[17px] font-poppins font-semibold text-slate-900 leading-snug text-center block">
                  Keeps track of your entire book of business, and so much more
                </span>
              </div>
            </div>
          </div>
        </section>
        </div>

        <MarketingFooter />
        </div>
      </div>
    </div>
  );
}